import React from "react";
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CarrierRowDTO } from "../../../api/admin/types";

interface CarriersChartProps {
  rows: CarrierRowDTO[];
}

// Single-hue palette so this chart doesn't fight with CATEGORY_COLORS used
// elsewhere on the page.
const BAR_COLOR = "#3949ab";

// Carrier names get long ("The Cincinnati Specialty Underwriters Insurance
// Company"). Recharts wraps the tick text to 2 lines which then collides
// with adjacent rows. Truncate on a single line and put the full name in a
// `<title>` so hover still surfaces it.
const MAX_LABEL_CHARS = 26;

const TruncatedTick = (props: {
  x?: string | number;
  y?: string | number;
  payload?: { value?: string };
}) => {
  const { x = 0, y = 0, payload } = props;
  const full = payload?.value ?? "";
  const displayed =
    full.length > MAX_LABEL_CHARS ? `${full.slice(0, MAX_LABEL_CHARS - 1)}…` : full;
  return (
    <text
      x={Number(x)}
      y={Number(y)}
      dy={4}
      textAnchor="end"
      fontSize={11}
      fill="#374151"
    >
      <title>{full}</title>
      {displayed}
    </text>
  );
};

const CarriersChart: React.FC<CarriersChartProps> = ({ rows }) => {
  const data = rows.map((r) => ({ label: r.name, count: r.count }));
  const height = Math.max(200, data.length * 24 + 60);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 16,
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>Top carriers</div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>
          By count of single-policy analyses in window
        </div>
      </div>
      {data.length === 0 ? (
        <div style={{ padding: 24, color: "#6b7280", textAlign: "center" }}>
          No carriers extracted in this window.
        </div>
      ) : (
        <div style={{ width: "100%", height }}>
          <ResponsiveContainer>
            <BarChart data={data} layout="vertical" margin={{ top: 8, right: 40, left: 8, bottom: 8 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="label"
                width={200}
                tick={TruncatedTick}
                interval={0}
              />
              <Tooltip />
              <Bar dataKey="count" isAnimationActive={false}>
                {data.map((_, i) => (
                  <Cell key={i} fill={BAR_COLOR} />
                ))}
                <LabelList dataKey="count" position="right" style={{ fontSize: 11, fontWeight: 600 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default CarriersChart;
